import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { alertsAPI, authAPI } from '../services/api';
import AppErrorBoundary from './errors/AppErrorBoundary';
import SidebarNavigation from './layout/SidebarNavigation';
import TopHeaderBar from './layout/TopHeaderBar';
import { AppFooter } from './design-system';
import { AppSessionGateFallback } from '../routing/AppRouteFallbacks';
import { ActionFeedbackProvider } from '../context/ActionFeedbackContext';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import PressFeedbackRoot from './PressFeedbackRoot';
import { getRememberLoginPreference } from '../utils/rememberLogin';

const SESSION_VERIFY_TIMEOUT_MS = 20_000;

function AppShellLayout() {
  const { effectiveCollapsed } = useSidebar();
  const { user, syncSessionFromServer, sessionReady } = useAuth();
  const location = useLocation();

  const [appSessionVerified, setAppSessionVerified] = useState(false);
  const [sessionGateState, setSessionGateState] = useState('loading');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertSummary, setAlertSummary] = useState({
    openCount: 0,
    byType: {},
    bySeverity: {},
    latest: [],
  });
  const alertsRef = useRef(null);
  const verifyRunRef = useRef(0);
  const sessionGateHandledRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const verifySession = async () => {
    const runId = ++verifyRunRef.current;
    setSessionGateState('loading');
    setAppSessionVerified(false);

    const nextPath = `${location.pathname}${location.search}`;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      if (verifyRunRef.current === runId) {
        setSessionGateState('timeout');
      }
    }, SESSION_VERIFY_TIMEOUT_MS);

    try {
      if (getRememberLoginPreference()) {
        await authAPI.refresh().catch(() => {});
      }
      const ok = await syncSessionFromServer();
      if (verifyRunRef.current !== runId) return;
      if (timedOut) return;
      if (!ok) {
        setSessionGateState('redirecting');
        window.location.assign(`/login?next=${encodeURIComponent(nextPath)}`);
        return;
      }
      setAppSessionVerified(true);
      setSessionGateState('ready');
    } catch {
      if (verifyRunRef.current !== runId || timedOut) return;
      setSessionGateState('error');
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    if (!sessionReady || sessionGateHandledRef.current) return undefined;
    sessionGateHandledRef.current = true;

    if (userRef.current) {
      setAppSessionVerified(true);
      setSessionGateState('ready');
      return undefined;
    }

    verifySession();
    // Run once after auth boot — do not re-verify on client nav or user object updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, syncSessionFromServer]);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const s = await alertsAPI.summary();
        if (!cancelled) setAlertSummary(s || { openCount: 0 });
      } catch {
        if (!cancelled)
          setAlertSummary({
            openCount: 0,
            totalOpenCount: 0,
            byType: {},
            bySeverity: {},
            latest: [],
          });
      }
    }
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (alertsRef.current && !alertsRef.current.contains(e.target))
        setAlertsOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const acknowledgeAlert = async (id) => {
    try {
      await alertsAPI.updateStatus(id, 'acknowledged');
      setAlertSummary((prev) => ({
        ...prev,
        openCount: Math.max(0, (prev.openCount || 1) - 1),
        latest: (prev.latest || []).filter((a) => a.id !== id),
      }));
    } catch {
      /* noop */
    }
  };

  if (!appSessionVerified) {
    if (sessionGateState === 'error') {
      return (
        <AppSessionGateFallback
          message="We could not verify your session. Please try again or sign in."
          onRetry={verifySession}
        />
      );
    }
    if (sessionGateState === 'timeout') {
      return (
        <AppSessionGateFallback
          message="Session verification is taking longer than expected."
          onRetry={verifySession}
        />
      );
    }
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm"
        style={{ background: 'var(--bg-dashboard)', color: 'var(--text-muted)' }}
        aria-busy="true"
      >
        Loading…
      </div>
    );
  }

  return (
    <ActionFeedbackProvider>
    <PressFeedbackRoot
      className="flex h-screen overflow-hidden"
      style={{ background: 'var(--bg-base)' }}
    >
      <aside
        className={`app-sidebar fixed lg:relative inset-y-0 left-0 z-50 flex flex-col shrink-0 transform transition-transform lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }${effectiveCollapsed ? ' app-sidebar--collapsed' : ''}`}
      >
        <SidebarNavigation
          user={user}
          onClose={() => setSidebarOpen(false)}
          onNavClick={() => setSidebarOpen(false)}
        />
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden app-main-scroll app-dashboard-backdrop">
        <TopHeaderBar
          alertSummary={alertSummary}
          alertsOpen={alertsOpen}
          alertsRef={alertsRef}
          onMenuOpen={() => setSidebarOpen(true)}
          onAlertsToggle={() => setAlertsOpen((v) => !v)}
          onAcknowledge={acknowledgeAlert}
        />

        <main className="flex-1 overflow-y-auto flex flex-col min-h-0">
          <div className="flex-1">
            <AppErrorBoundary context="app-outlet" compact showDashboardLink>
              <Outlet />
            </AppErrorBoundary>
          </div>
          <AppFooter />
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          style={{ background: 'var(--overlay-scrim)' }}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </PressFeedbackRoot>
    </ActionFeedbackProvider>
  );
}

export default function AppShell() {
  return (
    <SidebarProvider>
      <AppShellLayout />
    </SidebarProvider>
  );
}
